import type { UserPrivate } from '@repo/types/users';

import { isAfter, parseISO, subHours } from 'date-fns';

/** Lifetime of a signup invite link; mirrors `User.prototype.sendInvite` in the backend. */
export const INVITE_VALIDITY_DAYS = 30;

/**
 * Signup invite state for a user, derived from `last_login` and `authTokenExpiry`.
 * - `signed-up`: the user accepted the invite, there is no link to track.
 * - `valid`: the latest link still works until `expiresAt`; resending invalidates it. `sentAt` is when its token was generated.
 * - `expired`: the latest link stopped working at `expiresAt`.
 * - `none`: no link has been issued (e.g. created without an invite email).
 */
export type InviteStatus = { state: 'signed-up' } | { state: 'none' } | { state: 'valid'; expiresAt: Date; sentAt: Date } | { state: 'expired'; expiresAt: Date };

export function getInviteStatus(user: Pick<UserPrivate, 'last_login' | 'authTokenExpiry'>, now: Date = new Date()): InviteStatus {
  if (user.last_login) return { state: 'signed-up' };
  if (!user.authTokenExpiry) return { state: 'none' };

  const expiresAt = parseISO(user.authTokenExpiry);
  if (!isAfter(expiresAt, now)) return { state: 'expired', expiresAt };
  return { state: 'valid', expiresAt, sentAt: subHours(expiresAt, INVITE_VALIDITY_DAYS * 24) };
}
